import React from 'react';
import { IconType, IntentProps, Responsive, useFullTheme } from '~/design';
import { InternalText } from '~/internal/InternalText';
import { useResponsive } from '~/utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps extends IntentProps {
  /** Alt text for image avatars. */
  alt?: string;

  /**
   * Only set to true if the avatar is decorative. Please check the definition of decorative here:
   * https://www.w3.org/WAI/tutorials/images/decorative/
   */
  'aria-hidden'?: boolean;

  /** Accessible label for non-decorative icon avatars. */
  'aria-label'?: string;

  /** The emphasis of the avatar against its background. (default: contained) */
  emphasis?: 'contained' | 'highlighted' | 'outlined';

  /** The icon that appears within the avatar. */
  icon?: IconType;

  /** Image to render as the avatar. */
  image?: string;

  /** Initials to show as the avatar. */
  initials?: string;

  /** The size of the avatar. */
  size?: AvatarSize | Responsive<AvatarSize>;

  /**
   * The style of the avatar.
   * @deprecated Use `emphasis` instead.
   */
  variant?: 'contained' | 'highlighted' | 'outlined';
}

export function Avatar({
  alt,
  'aria-hidden': ariaHidden = false,
  'aria-label': ariaLabel,
  emphasis: _emphasis = 'contained',
  icon: Icon,
  image,
  initials,
  intent = 'primary',
  size = 'md',
  variant,
}: AvatarProps) {
  if ([Icon, image, initials].filter(Boolean).length !== 1) {
    throw new Error(
      'Exactly one of the following props must be supplied: `icon`, `image`, `initials`.',
    );
  }
  if (!!image && !alt && !ariaHidden) {
    throw new Error(
      '`alt` prop must be supplied for image avatars unless `aria-hidden` prop = true.',
    );
  }
  if (ariaLabel?.length && (image || initials)) {
    throw new Error('`aria-label` prop should only be used for icon avatars.');
  }

  const emphasis = variant ?? _emphasis;
  const theme = useFullTheme();
  const responsive = useResponsive();

  const sizeMap = getSizeMap();

  const Tag = image ? 'img' : 'div';
  const imageProps = {
    alt,
    src: image,
  };

  let content;
  if (initials) {
    content = (
      <InternalText
        as="span"
        contrast={emphasis === 'contained' ? 'inverted' : undefined}
        intent={intent}
        variant={responsive.map(
          size,
          prop =>
            (
              ({
                xs: 'labelM',
                sm: 'titleS',
                md: 'titleM',
                lg: 'titleM',
                xl: 'titleL',
              }) as const
            )[prop],
        )}
      >
        {initials}
      </InternalText>
    );
  } else if (Icon) {
    content = (
      <Icon
        aria-label={ariaLabel}
        contrast={emphasis === 'contained' ? 'inverted' : 'mid'}
        intent={intent}
      />
    );
  }

  return (
    <Tag
      aria-hidden={!ariaHidden ? undefined : true}
      css={[
        {
          ...theme.borders.transparent,
          alignItems: 'center',
          backgroundColor: image
            ? undefined
            : emphasis === 'contained'
              ? {
                  magic: theme.colors.actionMagic,
                  negative: theme.colors.actionNegative,
                  positive: theme.colors.actionPositive,
                  primary: theme.colors.actionPrimary,
                  secondary: theme.colors.actionSecondary,
                  warning: theme.colors.actionWarning,
                }[intent]
              : {
                  magic: theme.colors.bgMagic,
                  negative: theme.colors.bgNegative,
                  positive: theme.colors.bgPositive,
                  primary: theme.colors.bgPrimary,
                  secondary: theme.colors.bgSecondary,
                  warning: theme.colors.bgWarning,
                }[intent],
          borderRadius: theme.borderRadiuses.circle,
          display: 'flex',
          justifyContent: 'center',
        },
        responsive.css({
          height: responsive.map(size, prop => prop && sizeMap[prop]),
          minWidth: responsive.map(size, prop => prop && sizeMap[prop]),
          width: responsive.map(size, prop => prop && sizeMap[prop]),
        }),
        image && {
          objectFit: 'cover',
        },
        emphasis === 'outlined' && {
          ...theme.borders[intent],
        },
      ]}
      {...(image ? imageProps : {})}
    >
      {content}
    </Tag>
  );
}

export function getSizeMap() {
  return {
    xs: 32,
    sm: 48,
    md: 56,
    lg: 64,
    xl: 96,
  } as const;
}
